import { ArdoqId } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { selectedModule$ } from 'appContainer/selectedModule$';
import { AppModules } from 'appContainer/types';
import { isEqual } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
} from 'rxjs';
import { isRouteActive } from 'router/declarative/filterRouteActive';
import {
  AppLocation,
  AppRouterLocation,
  location$,
} from 'router/declarative/location$';
import { ComponentOverviewPageLocation } from './types';

export const COMPONENT_OVERVIEW_PAGE_ROUTE = '/component-overview/:componentId';

type PageRouterLocation = AppLocation<typeof COMPONENT_OVERVIEW_PAGE_ROUTE>;

export const toComponentOverviewPageLocation = ({
  params,
  searchParams,
}: PageRouterLocation): ComponentOverviewPageLocation => ({
  componentId: params.componentId,
  surveyId: searchParams?.surveyId ?? null,
  showNavigator: searchParams?.showNavigator ?? false,
});

/**
 * The router location of the page while it is open, `null` while it is closed.
 *
 * `location$` keeps the last location it matched for as long as the app is in a
 * legacy module, so the selected module is what says whether the page is still
 * open.
 */
export const toOpenPageLocation = (
  location: AppRouterLocation,
  selectedModule: AppModules
): PageRouterLocation | null => {
  if (selectedModule !== AppModules.COMPONENT_OVERVIEW_PAGE) return null;
  if (!isRouteActive(location, COMPONENT_OVERVIEW_PAGE_ROUTE)) return null;
  return location;
};

/**
 * For code that navigates relative to the page, which must not act on a
 * location the user has already left.
 */
export const openPageRouterLocation$: Observable<PageRouterLocation | null> =
  combineLatest([location$, selectedModule$]).pipe(
    map(([location, { selectedModule }]) =>
      toOpenPageLocation(location, selectedModule)
    )
  );

/**
 * The `null` between two visits is what the streams below deduplicate against,
 * so reopening the same component reads as a new visit rather than as no change.
 */
const openPageLocation$: Observable<ComponentOverviewPageLocation | null> =
  openPageRouterLocation$.pipe(
    map(location => location && toComponentOverviewPageLocation(location)),
    distinctUntilChanged(isEqual)
  );

export const componentOverviewPageLocation$: Observable<ComponentOverviewPageLocation> =
  openPageLocation$.pipe(filter(isTruthy));

export const componentOverviewPageComponentId$: Observable<ArdoqId> =
  openPageLocation$.pipe(
    map(location => location?.componentId ?? null),
    distinctUntilChanged(),
    filter(isTruthy)
  );

/**
 * Emits once each time the page opens, for data the page needs that does not
 * depend on which component is open.
 */
export const componentOverviewPageOpened$: Observable<void> =
  openPageRouterLocation$.pipe(
    map(location => location !== null),
    distinctUntilChanged(),
    filter(isTruthy),
    map(() => undefined)
  );

export const componentOverviewPageSurveyId$: Observable<ArdoqId | null> =
  componentOverviewPageLocation$.pipe(
    map(({ surveyId }) => surveyId),
    distinctUntilChanged()
  );

/**
 * The location that carries `surveyId`, or `null` when the URL already says the
 * same.
 */
export const toSurveyIdNavigation = (
  location: PageRouterLocation,
  surveyId: ArdoqId | null
): PageRouterLocation | null => {
  if ((location.searchParams?.surveyId ?? null) === surveyId) return null;
  return {
    ...location,
    searchParams: {
      ...location.searchParams,
      surveyId: surveyId ?? undefined,
      broadcastInstanceId: surveyId
        ? location.searchParams?.broadcastInstanceId
        : undefined,
    },
  };
};
